import React from "react";
import { useReveal } from "../../hooks/useReveal";
import { Ornament, StarGlyph, CuneiformMotif } from "../Ornaments";

export const TheRiff = () => {
  const head = useReveal();
  const quote = useReveal();
  const body = useReveal();

  return (
    <section
      id="riff"
      data-testid="riff-section"
      className="bg-elevated-section"
      style={{ position: "relative", padding: "7rem 1.5rem", overflow: "hidden" }}
    >
      <div className="gold-glow" style={{ width: 650, height: 650, top: -180, left: -180, opacity: 0.5 }} />
      <div className="violet-glow" style={{ width: 600, height: 600, bottom: -200, right: -120, opacity: 0.55 }} />

      <div className="section-inner" style={{ maxWidth: 1100 }}>
        <div ref={head.ref} className={`reveal ${head.inView ? "in-view" : ""}`} style={{ textAlign: "center", maxWidth: 780, margin: "0 auto 3.5rem" }}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Ornament centered />
          </div>
          <p className="font-heading" style={{ color: "var(--gold)", fontSize: "0.7rem", letterSpacing: "0.4em", marginBottom: "1rem" }}>
            Music &amp; Motion — The Riff
          </p>
          <h2 className="section-title" data-testid="riff-title">
            Find the Riff. Repeat It. <br /> Refine It — and It&rsquo;s Yours.
          </h2>
        </div>

        <div className="energetics-grid" style={{ display: "grid", gridTemplateColumns: "1fr", gap: "3rem", alignItems: "start" }}>
          {/* Video — spoken transmission with captions */}
          <div ref={body.ref} className={`reveal ${body.inView ? "in-view" : ""}`}>
            <figure
              className="charge-transmission"
              data-testid="riff-transmission"
              style={{ margin: "0 auto", maxWidth: 340 }}
            >
              <p
                className="font-heading"
                style={{
                  color: "var(--gold)",
                  fontSize: "0.6rem",
                  letterSpacing: "0.42em",
                  marginBottom: "0.85rem",
                  textAlign: "center",
                  textTransform: "uppercase",
                }}
              >
                ✦ &nbsp; Transmission &nbsp; ✦
              </p>
              <div className="charge-transmission-frame">
                <video
                  src="/media/riff-music-motion.mp4"
                  poster="/media/riff-music-motion-poster.jpg"
                  controls
                  playsInline
                  preload="metadata"
                  data-testid="riff-video"
                />
              </div>
              <figcaption
                className="font-heading"
                style={{
                  color: "var(--text-dim)",
                  fontSize: "0.58rem",
                  letterSpacing: "0.35em",
                  marginTop: "0.85rem",
                  textAlign: "center",
                  textTransform: "uppercase",
                }}
              >
                Sound On &nbsp;·&nbsp; Captions Included
              </figcaption>
            </figure>
          </div>

          {/* Quote card — verbatim from the transmission */}
          <figure
            ref={quote.ref}
            className={`reveal ${quote.inView ? "in-view" : ""}`}
            data-testid="riff-quote"
            style={{
              position: "relative",
              margin: 0,
              background: "linear-gradient(180deg, rgba(20,16,12,0.85) 0%, rgba(10,8,6,0.92) 100%)",
              border: "1px solid rgba(201, 168, 76, 0.25)",
              borderLeft: "3px solid var(--gold)",
              padding: "2.5rem 2rem",
            }}
          >
            <div style={{ position: "absolute", top: 14, right: 18, opacity: 0.5 }} aria-hidden="true">
              <StarGlyph size={14} />
            </div>
            <p className="font-heading" style={{ color: "var(--gold)", fontSize: "0.65rem", letterSpacing: "0.4em", marginBottom: "1.25rem" }}>
              In His Own Words
            </p>
            <blockquote
              style={{
                margin: 0,
                fontFamily: "var(--font-body)",
                fontStyle: "italic",
                color: "var(--text)",
                fontSize: "clamp(1.05rem, 1.4vw, 1.18rem)",
                lineHeight: 1.75,
              }}
            >
              <p style={{ marginBottom: "1rem" }}>
                <span style={{ color: "var(--gold-bright)" }}>&ldquo;</span>Music and motion really go
                together — especially at elite levels. It&rsquo;s kind of like jazz. It&rsquo;s kind of like
                jamming. You play around using your skills until you find a riff. And once you&rsquo;ve got
                that riff, you keep repeating it. Then it turns into a pattern.
              </p>
              <p style={{ marginBottom: "1rem" }}>
                Making a song, finding a song — that&rsquo;s essentially the same pattern as
                <span style={{ color: "var(--gold-bright)" }}> finding and building your signature</span>,
                finding and building your form.
              </p>
              <p style={{ marginBottom: 0 }}>
                Motion data is the jazz and jamming. Playing around until you find a riff. Then with
                the riff, you repeat it. And through play, you refine it —
                <span style={{ color: "var(--gold-bright)" }}> and it&rsquo;s yours.&rdquo;</span>
              </p>
            </blockquote>
            <div style={{ marginTop: "1.5rem", opacity: 0.55 }}>
              <CuneiformMotif width={140} />
            </div>

            <div
              style={{
                marginTop: "2rem",
                paddingTop: "1.5rem",
                borderTop: "1px solid rgba(201,168,76,0.18)",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1.5rem",
              }}
            >
              <div data-testid="riff-card-find">
                <p className="font-heading" style={{ color: "var(--gold)", fontSize: "0.68rem", letterSpacing: "0.32em", marginBottom: "0.5rem" }}>
                  Find the Riff
                </p>
                <p style={{ color: "var(--text-muted)", fontSize: "0.92rem", lineHeight: 1.65 }}>
                  Play until a pattern surfaces. That pattern is data.
                </p>
              </div>
              <div data-testid="riff-card-refine">
                <p className="font-heading" style={{ color: "var(--gold)", fontSize: "0.68rem", letterSpacing: "0.32em", marginBottom: "0.5rem" }}>
                  Refine Through Play
                </p>
                <p style={{ color: "var(--text-muted)", fontSize: "0.92rem", lineHeight: 1.65 }}>
                  Repeat it. Shape it. Until the pattern is unmistakably yours.
                </p>
              </div>
            </div>
          </figure>
        </div>
      </div>
    </section>
  );
};
